/**
 * usePanelData — the single React entry point for panel data. Given a
 * {@link DataBinding} and a range, it opens the right provider (nats/doris/
 * hybrid) and returns per-channel series + latest values + loading, plus a
 * `loadDetail` for full-resolution zoom. Panels never branch on source mode.
 *
 * Updates are flushed on an interval so a busy live stream doesn't re-render on
 * every message.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { DataBinding, ResolvedRange } from './spec';
import type { SeriesPoint, SeriesSubscription, ProviderContext } from './providers/SeriesProvider';
import { getProvider } from './providers/registry';

const MAX_POINTS = 5000;
const FLUSH_MS = 400;

export interface PanelData {
  series: Map<string, SeriesPoint[]>;
  latest: Map<string, number>;
  /** Timestamp (ms since epoch) of the most recent point seen per channel. */
  lastUpdate: Map<string, number>;
  loading: boolean;
  loadDetail: (startMs: number, endMs: number) => Promise<void>;
}

export function usePanelData(binding: DataBinding, range: ResolvedRange): PanelData {
  const [series, setSeries] = useState<Map<string, SeriesPoint[]>>(new Map());
  const [latest, setLatest] = useState<Map<string, number>>(new Map());
  const [lastUpdate, setLastUpdate] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(false);

  const seriesRef = useRef<Map<string, SeriesPoint[]>>(new Map());
  const latestRef = useRef<Map<string, number>>(new Map());
  const lastUpdateRef = useRef<Map<string, number>>(new Map());
  const dirtyRef = useRef(false);
  const subRef = useRef<SeriesSubscription | null>(null);
  const ctxRef = useRef<ProviderContext | null>(null);

  const rangeKey = range.kind === 'live' ? 'live' : `${range.startMs}-${range.endMs}`;
  const bindingKey = `${binding.machineRef}|${binding.source.mode}|${JSON.stringify(binding.channels.map((c) => ({ k: c.key, r: c.nats?.regex })))}`;

  useEffect(() => {
    const provider = getProvider(binding.source.mode);
    const ctx: ProviderContext = {
      machineBK: binding.machineRef,
      channels: binding.channels,
      binding,
    };
    ctxRef.current = ctx;

    seriesRef.current = new Map();
    latestRef.current = new Map();
    lastUpdateRef.current = new Map();
    dirtyRef.current = true;

    const flush = setInterval(() => {
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      setSeries(new Map(seriesRef.current));
      setLatest(new Map(latestRef.current));
      setLastUpdate(new Map(lastUpdateRef.current));
    }, FLUSH_MS);

    subRef.current = provider.open(ctx, range, (patch) => {
      if (patch.loading !== undefined) setLoading(patch.loading);
      if (patch.reset) {
        for (const key of Object.keys(patch.byChannel)) seriesRef.current.set(key, []);
      }
      for (const [key, pts] of Object.entries(patch.byChannel)) {
        const arr = seriesRef.current.get(key) ?? [];
        // Loop, not `arr.push(...pts)`: a spread passes each point as its own
        // argument, so a large Doris patch overflows the engine's argument limit
        // ("Maximum call stack size exceeded").
        for (const p of pts) arr.push(p);
        if (arr.length > MAX_POINTS) arr.splice(0, arr.length - MAX_POINTS);
        seriesRef.current.set(key, arr);
        const lastT = arr[arr.length - 1]?.t;
        if (lastT !== undefined) lastUpdateRef.current.set(key, lastT);
      }
      if (patch.latest) {
        for (const [key, v] of Object.entries(patch.latest)) latestRef.current.set(key, v);
      }
      dirtyRef.current = true;
    });

    return () => {
      clearInterval(flush);
      subRef.current?.close();
      subRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bindingKey, rangeKey]);

  const loadDetail = useCallback(async (startMs: number, endMs: number) => {
    const provider = getProvider(binding.source.mode);
    const ctx = ctxRef.current;
    if (!provider.loadDetail || !ctx) return;
    setLoading(true);
    try {
      const patch = await provider.loadDetail(ctx, startMs, endMs);
      // Replace points inside the window with the full-resolution ones.
      for (const [key, pts] of Object.entries(patch.byChannel)) {
        const existing = seriesRef.current.get(key) ?? [];
        const outside = existing.filter((p) => p.t < startMs || p.t > endMs);
        const merged = outside.concat(pts);
        merged.sort((a, b) => a.t - b.t);
        seriesRef.current.set(key, merged);
      }
      setSeries(new Map(seriesRef.current));
    } catch (err) {
      // A failed detail load (e.g. response too large / chunk truncated) must
      // not crash the panel — keep the existing aggregated data on screen.
      console.error('usePanelData: loadDetail failed', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bindingKey]);

  return { series, latest, lastUpdate, loading, loadDetail };
}
